import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Descriptions,
  Form,
  Input,
  List,
  Space,
  Tag,
  Typography,
  message,
} from 'antd';
import { CheckOutlined, RobotOutlined } from '@ant-design/icons';
import { generateMotionActionConfigCandidate } from '@/services/actionConfigManagement';

const { Paragraph, Text } = Typography;
const { TextArea } = Input;

function isSuccess(response) {
  return response && (response.code === '00000' || response.code === '0');
}

function issueList(title, issues, type) {
  if (!issues?.length) return null;
  return (
    <Alert
      type={type}
      showIcon
      message={`${title}（${issues.length}）`}
      description={
        <List
          size="small"
          dataSource={issues}
          renderItem={(item) => (
            <List.Item>
              <Space direction="vertical" size={0}>
                <Text>{item.message}</Text>
                <Text type="secondary">
                  {item.jsonPointer || '$'} · {item.code}
                </Text>
              </Space>
            </List.Item>
          )}
        />
      }
    />
  );
}

export default function MotionConfigAiGenerator({
  actionId,
  actionName,
  currentConfig,
  onApply,
}) {
  const [form] = Form.useForm();
  const [includeCurrent, setIncludeCurrent] = useState(!!currentConfig);
  const [generating, setGenerating] = useState(false);
  const [candidate, setCandidate] = useState(null);

  const generate = async () => {
    const values = await form.validateFields();
    setGenerating(true);
    try {
      const response = await generateMotionActionConfigCandidate({
        actionId: Number(actionId),
        actionName,
        actionDescription: values.actionDescription,
        generationRequirement: values.generationRequirement,
        currentConfigContent: includeCurrent ? currentConfig : undefined,
      });
      if (!isSuccess(response)) {
        message.error(response?.msg || 'AI 候选生成失败');
        return;
      }
      setCandidate(response.data);
      if (response.data?.passed) {
        message.success('配置已生成，可直接采用到手动编排区');
      } else {
        message.warning('候选已生成，但未通过静态校验');
      }
    } catch (error) {
      message.error(error?.message || 'AI 候选生成失败');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Alert
        type="info"
        showIcon
        message="AI 生成不会自动保存或发布"
        description="动作名称、动作说明、可选当前配置和生成要求会发送到公司统一 Power LLM。生成结果会先经过静态校验；通过后可直接采用并继续手动调整。"
      />
      <Card size="small" title="生成要求" extra={<RobotOutlined />}>
        <Form form={form} layout="vertical">
          <Form.Item label="动作" style={{ marginBottom: 12 }}>
            <Input value={`${actionName || '动作'}（${actionId}）`} disabled />
          </Form.Item>
          <Form.Item
            name="actionDescription"
            label="动作客观说明补充（可选）"
            tooltip="服务端会自动补充动作库资料；这里只补充起始姿势、运动方向或左右侧等客观事实"
          >
            <TextArea
              rows={4}
              maxLength={8000}
              showCount
              placeholder="例如：站姿双侧深蹲，从直立开始屈髋屈膝下蹲，再伸髋伸膝回到直立；侧面拍摄。"
            />
          </Form.Item>
          <Form.Item name="generationRequirement" label="本次生成要求">
            <TextArea
              rows={3}
              maxLength={4000}
              showCount
              placeholder="例如：识别完整动作周期，保留膝关节活动范围、节奏和躯干稳定性客观特征。"
            />
          </Form.Item>
          <Form.Item style={{ marginBottom: 12 }}>
            <Checkbox
              checked={includeCurrent}
              disabled={!currentConfig}
              onChange={(event) => setIncludeCurrent(event.target.checked)}
            >
              基于当前配置调整
            </Checkbox>
          </Form.Item>
          <Button
            type="primary"
            icon={<RobotOutlined />}
            loading={generating}
            onClick={generate}
          >
            生成候选配置
          </Button>
        </Form>
      </Card>

      {candidate && (
        <Card
          size="small"
          title="候选配置"
          extra={
            <Tag color={candidate.passed ? 'success' : 'error'}>
              {candidate.passed ? '静态校验通过' : '静态校验未通过'}
            </Tag>
          }
        >
          <Space direction="vertical" size={12} style={{ width: '100%' }}>
            <Descriptions size="small" column={3} bordered>
              <Descriptions.Item label="模型">
                {candidate.model || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="上下文">
                {candidate.contextVersion || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="配置哈希">
                {candidate.configHash || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="候选 ID">
                {candidate.candidateId || '-'}
              </Descriptions.Item>
            </Descriptions>
            {issueList('校验错误', candidate.errors, 'error')}
            {issueList('校验提醒', candidate.warnings, 'warning')}
            <Paragraph
              copyable
              style={{
                maxHeight: 520,
                overflow: 'auto',
                padding: 16,
                marginBottom: 0,
                whiteSpace: 'pre-wrap',
                background: '#0f172a',
                borderRadius: 6,
                color: '#d1fae5',
                fontFamily: 'SFMono-Regular, Consolas, monospace',
                fontSize: 13,
              }}
            >
              {candidate.candidateConfig || '模型未返回候选内容'}
            </Paragraph>
            <Button
              type="primary"
              icon={<CheckOutlined />}
              disabled={!candidate.passed || !candidate.candidateConfig}
              onClick={() => onApply(candidate.candidateConfig)}
            >
              采用并进入手动编排
            </Button>
          </Space>
        </Card>
      )}
    </Space>
  );
}

MotionConfigAiGenerator.propTypes = {
  actionId: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    .isRequired,
  actionName: PropTypes.string,
  currentConfig: PropTypes.string,
  onApply: PropTypes.func.isRequired,
};

MotionConfigAiGenerator.defaultProps = {
  actionName: '',
  currentConfig: '',
};
